import React, { useState, useEffect, useCallback } from 'react';
import { ShoppingBag, Star, Sparkles, MapPin, BellOff, Bell } from 'lucide-react';

const buyers = [
  { name: 'Sarah M.', location: 'Los Angeles, CA', country: 'USA', flag: '\u{1F1FA}\u{1F1F8}' },
  { name: 'Jennifer K.', location: 'New York, NY', country: 'USA', flag: '\u{1F1FA}\u{1F1F8}' },
  { name: 'Amanda R.', location: 'Austin, TX', country: 'USA', flag: '\u{1F1FA}\u{1F1F8}' },
  { name: 'Michelle L.', location: 'Seattle, WA', country: 'USA', flag: '\u{1F1FA}\u{1F1F8}' },
  { name: 'Emily T.', location: 'Miami, FL', country: 'USA', flag: '\u{1F1FA}\u{1F1F8}' },
  { name: 'Jessica W.', location: 'Chicago, IL', country: 'USA', flag: '\u{1F1FA}\u{1F1F8}' },
  { name: 'Ashley B.', location: 'Denver, CO', country: 'USA', flag: '\u{1F1FA}\u{1F1F8}' },
  { name: 'Rachel H.', location: 'Phoenix, AZ', country: 'USA', flag: '\u{1F1FA}\u{1F1F8}' },
  { name: 'Emma S.', location: 'London', country: 'UK', flag: '\u{1F1EC}\u{1F1E7}' },
  { name: 'Sophie L.', location: 'Paris', country: 'France', flag: '\u{1F1EB}\u{1F1F7}' },
  { name: 'Maria G.', location: 'Madrid', country: 'Spain', flag: '\u{1F1EA}\u{1F1F8}' },
  { name: 'Anna K.', location: 'Berlin', country: 'Germany', flag: '\u{1F1E9}\u{1F1EA}' },
  { name: 'Yuki T.', location: 'Tokyo', country: 'Japan', flag: '\u{1F1EF}\u{1F1F5}' },
  { name: 'Mei L.', location: 'Singapore', country: 'Singapore', flag: '\u{1F1F8}\u{1F1EC}' },
  { name: 'Priya S.', location: 'Mumbai', country: 'India', flag: '\u{1F1EE}\u{1F1F3}' },
  { name: 'Olivia M.', location: 'Sydney', country: 'Australia', flag: '\u{1F1E6}\u{1F1FA}' },
  { name: 'Isabella R.', location: 'Toronto', country: 'Canada', flag: '\u{1F1E8}\u{1F1E6}' },
  { name: 'Luna V.', location: 'Mexico City', country: 'Mexico', flag: '\u{1F1F2}\u{1F1FD}' },
  { name: 'Sofia C.', location: 'S\u00e3o Paulo', country: 'Brazil', flag: '\u{1F1E7}\u{1F1F7}' },
  { name: 'Fatima A.', location: 'Dubai', country: 'UAE', flag: '\u{1F1E6}\u{1F1EA}' },
  { name: 'Aisha N.', location: 'Cape Town', country: 'South Africa', flag: '\u{1F1FF}\u{1F1E6}' },
  { name: 'Chen W.', location: 'Hong Kong', country: 'China', flag: '\u{1F1ED}\u{1F1F0}' },
  { name: 'Kim J.', location: 'Seoul', country: 'South Korea', flag: '\u{1F1F0}\u{1F1F7}' },
  { name: 'Astrid L.', location: 'Stockholm', country: 'Sweden', flag: '\u{1F1F8}\u{1F1EA}' },
];

const products = [
  { name: 'Amethyst Crystal Cluster', price: '$275', type: 'product', icon: '\u{1F49C}' },
  { name: 'Rose Quartz Heart', price: '$89', type: 'product', icon: '\u{1F497}' },
  { name: 'Black Tourmaline Tower', price: '$150', type: 'product', icon: '\u{1F5A4}' },
  { name: 'Selenite Wand', price: '$65', type: 'product', icon: '\u{1F90D}' },
  { name: 'Citrine Abundance Stone', price: '$120', type: 'product', icon: '\u{1F49B}' },
  { name: 'Clear Quartz Point', price: '$95', type: 'product', icon: '\u{1F52E}' },
  { name: 'Smokey Quartz Healing Wand', price: '$150', type: 'product', icon: '\u{1F90E}' },
  { name: 'Monthly Wellness Membership', price: '$300/mo', type: 'membership', icon: '\u2728' },
  { name: 'Selenite Healing Mastery Webinar', price: '$800', type: 'webinar', icon: '\u{1F393}' },
  { name: 'Monthly Spiritual Package', price: '$300/mo', type: 'membership', icon: '\u{1F319}' },
];

const timeAgo = () => {
  const times = ['just now', '2 minutes ago', '5 minutes ago', '8 minutes ago', '12 minutes ago', '15 minutes ago', '23 minutes ago', '1 hour ago'];
  return times[Math.floor(Math.random() * times.length)];
};

const MUTE_DELAY_MS = 5 * 60 * 1000; // 5 minutes

const BuyerNotifications = () => {
  const [notification, setNotification] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [muted, setMuted] = useState(false);
  const [canMute, setCanMute] = useState(false);

  const generateNotification = useCallback(() => {
    const buyer = buyers[Math.floor(Math.random() * buyers.length)];
    const product = products[Math.floor(Math.random() * products.length)];
    return { buyer, product, time: timeAgo() };
  }, []);

  // Enable mute button after 5 minutes
  useEffect(() => {
    const timer = setTimeout(() => setCanMute(true), MUTE_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (muted) return;

    let hideTimer;
    let nextTimer;

    const showNotification = () => {
      setNotification(generateNotification());
      setIsVisible(true);
      hideTimer = setTimeout(() => {
        setIsVisible(false);
        const nextDelay = 15000 + Math.random() * 30000;
        nextTimer = setTimeout(showNotification, nextDelay);
      }, 5000);
    };

    const initialTimer = setTimeout(showNotification, 5000);

    return () => {
      clearTimeout(initialTimer);
      clearTimeout(hideTimer);
      clearTimeout(nextTimer);
    };
  }, [muted, generateNotification]);

  const handleMute = () => {
    setMuted(true);
    setIsVisible(false);
  };

  const handleUnmute = () => {
    setMuted(false);
  };

  // Mute toggle (only after 5 min)
  if (canMute && muted) {
    return (
      <button
        onClick={handleUnmute}
        className="fixed bottom-6 left-6 z-50 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm border border-[#e8e4de] shadow-lg flex items-center justify-center hover:bg-[#f8f5ff] transition-all group"
        title="Turn notifications back on"
        data-testid="unmute-notifications-btn"
      >
        <BellOff className="w-4 h-4 text-[#7A7A9A] group-hover:text-[#9D4EDD] transition-colors" />
      </button>
    );
  }

  if (!notification || !isVisible) {
    return canMute && !muted ? (
      <button
        onClick={handleMute}
        className="fixed bottom-6 left-6 z-50 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm border border-[#e8e4de] shadow-lg flex items-center justify-center hover:bg-[#f8f5ff] transition-all group"
        title="Pause purchase notifications"
        data-testid="mute-notifications-btn"
      >
        <Bell className="w-4 h-4 text-[#7A7A9A] group-hover:text-[#9D4EDD] transition-colors" />
      </button>
    ) : null;
  }

  const { buyer, product, time } = notification;
  const isMembership = product.type === 'membership';
  const isWebinar = product.type === 'webinar';

  return (
    <div
      className={`fixed bottom-6 left-6 z-50 max-w-sm transition-all duration-500 ${
        isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-full'
      }`}
      data-testid="buyer-notification"
    >
      <div className={`rounded-2xl shadow-2xl overflow-hidden ${
        isMembership ? 'bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D]' :
        isWebinar ? 'bg-gradient-to-r from-[#FFB800] to-[#FF6B9D]' :
        'bg-white'
      }`}>
        <div className={`p-4 ${isMembership || isWebinar ? 'text-white' : ''}`}>
          <div className="flex items-start gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0 ${
              isMembership || isWebinar ? 'bg-white/20' : 'bg-gradient-to-br from-[#9D4EDD]/10 to-[#FF6B9D]/10'
            }`}>
              {product.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">{buyer.flag}</span>
                <span className={`font-semibold text-sm ${isMembership || isWebinar ? 'text-white' : 'text-[#1e1919]'}`}>{buyer.name}</span>
                {canMute && (
                  <button
                    onClick={handleMute}
                    className={`ml-auto p-1 rounded-full transition-all ${
                      isMembership || isWebinar ? 'hover:bg-white/20 text-white/60 hover:text-white' : 'hover:bg-[#f8f5ff] text-[#7A7A9A] hover:text-[#9D4EDD]'
                    }`}
                    title="Pause notifications"
                    data-testid="mute-inline-btn"
                  >
                    <BellOff className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <p className={`text-xs mb-1 flex items-center gap-1 ${isMembership || isWebinar ? 'text-white/80' : 'text-[#7A7A9A]'}`}>
                <MapPin className="w-3 h-3" />{buyer.location}
              </p>
              <p className={`text-sm font-medium ${isMembership || isWebinar ? 'text-white' : 'text-[#1e1919]'}`}>
                {isMembership ? 'Joined' : isWebinar ? 'Registered for' : 'Purchased'}
              </p>
              <p className={`text-sm truncate ${isMembership || isWebinar ? 'text-white/90' : 'text-[#9D4EDD]'}`}>{product.name}</p>
              <div className="flex items-center justify-between mt-2">
                <span className={`text-xs ${isMembership || isWebinar ? 'text-white/70' : 'text-[#7A7A9A]'}`}>{time}</span>
                <span className={`text-sm font-bold ${isMembership || isWebinar ? 'text-white' : 'text-[#00C853]'}`}>{product.price}</span>
              </div>
            </div>
          </div>
        </div>
        <div className={`px-4 py-2 text-xs flex items-center gap-2 ${
          isMembership || isWebinar ? 'bg-white/10 text-white/80' : 'bg-[#faf8f5] text-[#7A7A9A]'
        }`}>
          <Star className="w-3 h-3 fill-current text-[#FFB800]" />
          <span>Verified Purchase</span>
          <Sparkles className="w-3 h-3 ml-auto" />
        </div>
      </div>
    </div>
  );
};

export default BuyerNotifications;
